// ── Marcar un módulo como visto al entrar ────────────────────────────────────
// Va en UN solo lugar y escucha la ruta, en vez de un useEffect copiado en cada
// página. Dos razones concretas: entrar por URL directa o con el botón atrás no
// pasa por el clic del menú, y ocho copias del mismo efecto divergen en el
// primer cambio.
//
// La marca ANTERIOR queda disponible acá: es la que la lista usa para resaltar
// las filas que eran nuevas en esta visita. El badge se apaga al entrar, pero
// lo que había que mirar sigue señalado hasta que se actúe sobre ello.
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMarkNavSeen, type NavModule } from './useForms.js';

/** Primer segmento de la ruta → módulo. Las rutas de detalle cuelgan del mismo
 *  prefijo (`/reports/123`), así que entrar a un reporte también marca visto. */
const RUTA_A_MODULO: Record<string, NavModule> = {
  email: 'email',
  leads: 'leads',
  embudos: 'funnels',
  formularios: 'forms',
  proyectos: 'projects',
  reports: 'reports',
  tasks: 'tasks',
  mensajes: 'messages',
  facturacion: 'billing',
};

export function moduloDeRuta(pathname: string): NavModule | null {
  const primero = pathname.split('/').filter(Boolean)[0] ?? '';
  return RUTA_A_MODULO[primero] ?? null;
}

/**
 * Marca visto el módulo de la ruta actual, una vez por entrada.
 *
 * `marcados` evita repetir la llamada mientras se navega dentro del mismo
 * módulo (de la lista al detalle y de vuelta): sin eso, cada cambio de ruta
 * escribiría en la base y adelantaría la marca, y las filas resaltadas se
 * apagarían al abrir la primera.
 */
export interface MarcaDeModulo {
  previo: string;
  vistos: Set<string>;
  /** Marcados "no leído" a mano: resaltados aunque sean anteriores a `previo`. */
  noLeidos: Set<string>;
}

export function useNavSeenTracker(): Record<string, MarcaDeModulo> {
  const { pathname } = useLocation();
  const marcar = useMarkNavSeen();
  const marcados = useRef<Set<string>>(new Set());
  const [previos, setPrevios] = useState<Record<string, MarcaDeModulo>>({});

  useEffect(() => {
    const modulo = moduloDeRuta(pathname);
    if (modulo === null || marcados.current.has(modulo)) return;
    marcados.current.add(modulo);
    marcar.mutate(modulo, {
      onSuccess: r => { setPrevios(p => ({ ...p, [modulo]: { previo: r.previo, vistos: new Set(r.vistos), noLeidos: new Set(r.noLeidos ?? []) } })); },
      // Si falla, se saca de la lista para reintentar en la próxima entrada:
      // dejarlo marcado sin haber escrito deja el badge encendido para siempre.
      onError: () => { marcados.current.delete(modulo); },
    });
  // `marcar` es estable entre renders; incluirlo dispararía el efecto en cada
  // cambio de estado de la mutación.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return previos;
}
